import * as API from 'services/api';
import type { ApiResponse, ApiError } from 'services/api';

// =============================================================================
// GET /v2/slate-report/{movieId}/predictions
// =============================================================================
export namespace GetSlateReportPredictions {
  export interface Prediction {
    id: string;
    date: string;
    version: string;
    box_office_gross: number;
    box_office_multiple: number;
  }

  export type SortBy = 'box_office_gross' | 'box_office_multiple' | 'date';

  export interface Params {
    sort_by?: SortBy;
  }

  export type Response = ApiResponse<Prediction[]>;
  export type Error = ApiError;
}

export const getSlateReportPredictions = async (
  movieId: string,
  params?: GetSlateReportPredictions.Params,
): Promise<
  GetSlateReportPredictions.Response | GetSlateReportPredictions.Error
> => {
  try {
    const res = await API.get(
      `/v2/slate-report/${movieId}/predictions`,
      params,
    );
    return { data: res.data, status: res.status };
  } catch (error: any) {
    return {
      error: error.response?.data?.message || 'Failed to fetch predictions',
      status: error.response?.status || 500,
    };
  }
};

// =============================================================================
// GET /v2/slate-report/movies-search
// =============================================================================
export namespace GetSlateReportMoviesSearch {
  export interface Movie {
    id: string;
    title: string;
    production_year: number;
    release_date: string;
    genres: string[] | null;
    mpaa_rating: string;
    runtime: number;
    poster_url: string;
  }

  export interface PaginatedResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: Movie[];
  }

  export interface Params {
    limit?: number;
    offset?: number;
    title?: string;
  }

  export type Response = ApiResponse<PaginatedResponse>;
  export type Error = ApiError;
}

export const getSlateReportMoviesSearch = async (
  params?: GetSlateReportMoviesSearch.Params,
): Promise<
  GetSlateReportMoviesSearch.Response | GetSlateReportMoviesSearch.Error
> => {
  try {
    const res = await API.get('/v2/slate-report/movies-search', params);
    return { data: res.data, status: res.status };
  } catch (error: any) {
    return {
      error: error.response?.data?.message || 'Failed to search movies',
      status: error.response?.status || 500,
    };
  }
};

// =============================================================================
// POST /v2/slate-report
// =============================================================================
export namespace PostSlateReport {
  export interface RequestedDataItem {
    movie_id: string;
    prediction_id: string;
  }

  export interface RequestBody {
    requested_data: RequestedDataItem[];
  }

  export interface Location {
    name: string;
    city: string;
    region_state: string;
    country: string;
  }

  export interface Overview {
    producer: string[];
    line_producer: string[];
    executive_producer: string[];
    locations: Location[];
    distribution: string[];
  }

  export interface PlotData {
    value: string;
    score: number;
    script: string;
  }

  export interface BudgetData {
    value: number;
    score: number;
  }

  export interface ReleaseDateData {
    value: string;
    score: number;
  }

  export interface MpaaRatingData {
    value: string;
    score: number;
  }

  export interface AspectRatioData {
    value: string;
    score: number;
  }

  export interface RuntimeData {
    value: number;
    score: number;
  }

  export interface GenreData {
    value: string[];
    score: number;
  }

  export interface DirectorData {
    value: string[];
    score: number;
  }

  export interface ProducerData {
    value: string[];
    score: number;
  }

  export interface WriterData {
    value: string[];
    score: number;
  }

  export interface ActorData {
    value: string[];
    score: number;
  }

  export interface StarData {
    value: string[];
    score: number;
  }

  export interface User {
    id: number;
    name: string;
  }

  export interface PredictionItem {
    movie_id: string;
    prediction_id: string;
    title: string;
    overview: Overview;
    is_custom: boolean;
    version: string;
    database: string;
    algorithm: string;
    date: string;
    plot: PlotData;
    budget: BudgetData;
    release_date: ReleaseDateData;
    mpaa_rating: MpaaRatingData;
    aspect_ratio: AspectRatioData;
    runtime: RuntimeData;
    genre: GenreData;
    director: DirectorData;
    producer: ProducerData;
    writer: WriterData;
    actor: ActorData;
    star: StarData;
    collaboration: number;
    category_total: number;
    minimum_box_office_multiple: number;
    resulting_box_office_gross: number;
    user: User;
    poster_url: string;
  }

  export interface Summary {
    projects_analyzed: number;
    total_budgets_analyzed: number;
    total_box_office_gross_analyzed: number;
  }

  export interface ResponseData {
    predictions: PredictionItem[];
    summary: Summary;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const postSlateReport = async (
  requestBody: PostSlateReport.RequestBody,
): Promise<PostSlateReport.Response | PostSlateReport.Error> => {
  try {
    const res = await API.post('/v2/slate-report/', requestBody);
    return { data: res.data, status: res.status };
  } catch (error: any) {
    return {
      error: error.response?.data?.message || 'Failed to create slate report',
      status: error.response?.status || 500,
    };
  }
};
